import { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, MapPin, X } from 'lucide-react';
import axios from 'axios';
import { API } from '../constants/site';
import { resolveImg } from '../constants/siteImages';
import { useLanguage } from '../i18n/LanguageContext';
import { MaskedTitle, Overline, EASE } from '../components/motion';
import { useSEO } from '../components/SEO';

const FILTERS = ['all', 'pvc', 'garaj', 'automatizari'];

const navBtn = 'w-12 h-12 grid place-items-center rounded-full bg-white/10 text-white hover:bg-accent transition-colors duration-300 shrink-0';

export default function Portofoliu() {
  const { t } = useLanguage();
  const p = t('portfolioPage');
  useSEO('portofoliu');
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null); // { item, idx }

  useEffect(() => {
    axios.get(`${API}/portfolio`).then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const visible = filter === 'all' ? items : items.filter((i) => i.category === filter);

  const photos = lightbox ? (lightbox.item.images && lightbox.item.images.length ? lightbox.item.images : [lightbox.item.image]).map(resolveImg) : [];
  const step = useCallback((dir) => {
    setLightbox((lb) => (lb ? { ...lb, idx: (lb.idx + dir + photos.length) % photos.length } : lb));
  }, [photos.length]);

  useEffect(() => {
    if (!lightbox) return undefined;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [lightbox, step]);

  return (
    <main data-testid="portofoliu-page" className="max-w-[1440px] mx-auto px-6 md:px-12 pt-40 pb-24 md:pb-32">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
        <Overline className="mb-8">{p.overline}</Overline>
      </motion.div>
      <MaskedTitle
        lines={[p.title]}
        className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] mb-6 max-w-3xl"
      />
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
        className="text-smoke max-w-xl leading-relaxed mb-14"
      >
        {p.desc}
      </motion.p>

      <div data-testid="portfolio-filters" className="flex flex-wrap gap-2 mb-14">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`portfolio-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] border transition-all duration-300 ${
              filter === f
                ? 'bg-accent text-white border-accent'
                : 'border-line/20 text-smoke hover:border-accent/60 hover:text-ink'
            }`}
          >
            {p.filters[f]}
          </button>
        ))}
      </div>

      <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {visible.map((item, i) => (
            <motion.div
              layout
              key={item.id || item.title}
              data-testid={`portfolio-item-${i}`}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="relative overflow-hidden group cursor-pointer"
              onClick={() => setLightbox({ item, idx: 0 })}
            >
              <img
                src={resolveImg((item.images && item.images[0]) || item.image)}
                alt={item.title}
                loading="lazy"
                decoding="async"
                className={`w-full h-72 md:h-96 object-cover transition-transform duration-[1.2s] group-hover:scale-105 ${i % 3 === 1 ? 'clip-frame-alt' : 'clip-frame'}`}
              />
              <span className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/10 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="absolute bottom-0 left-0 right-0 p-6 text-white translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-accent block mb-2">
                  {p.filters[item.category]}
                </span>
                <span className="font-display text-xl font-medium block">{item.title}</span>
                <span className="text-smoke text-xs uppercase tracking-[0.15em] flex items-center gap-2 mt-2">
                  <MapPin size={13} className="text-accent" /> {item.location}
                </span>
              </span>
              {item.images && item.images.length > 1 && (
                <span data-testid={`portfolio-count-${i}`} className="absolute top-4 right-4 bg-black/60 text-white text-[10px] font-bold tracking-[0.15em] px-3 py-1.5 rounded-full">
                  {item.images.length} FOTO
                </span>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="portfolio-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
            onClick={() => setLightbox(null)}
          >
            <div className="flex items-center justify-between px-6 md:px-10 py-5 shrink-0">
              <span data-testid="lightbox-counter" className="text-white/60 text-xs font-bold tracking-[0.25em]">
                {lightbox.idx + 1} / {photos.length}
              </span>
              <button
                type="button"
                data-testid="lightbox-close"
                aria-label="Închide"
                onClick={() => setLightbox(null)}
                className="w-11 h-11 grid place-items-center rounded-full bg-white/10 text-white hover:bg-accent transition-colors duration-300"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 min-h-0 flex items-center justify-center gap-3 md:gap-6 px-3 md:px-8" onClick={(e) => e.stopPropagation()}>
              {photos.length > 1 && (
                <button type="button" data-testid="lightbox-prev" aria-label="Anterior" onClick={() => step(-1)} className={navBtn}>
                  <ChevronLeft size={22} />
                </button>
              )}
              <motion.img
                key={photos[lightbox.idx]}
                data-testid="lightbox-image"
                src={photos[lightbox.idx]}
                alt={lightbox.item.title}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, ease: EASE }}
                className="max-h-full max-w-full object-contain"
              />
              {photos.length > 1 && (
                <button type="button" data-testid="lightbox-next" aria-label="Următor" onClick={() => step(1)} className={navBtn}>
                  <ChevronRight size={22} />
                </button>
              )}
            </div>
            <div className="px-6 md:px-10 py-6 shrink-0 text-center" onClick={(e) => e.stopPropagation()}>
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-accent mb-1.5">{p.filters[lightbox.item.category]}</p>
              <p data-testid="lightbox-title" className="font-display text-lg md:text-xl text-white font-medium">{lightbox.item.title}</p>
              {lightbox.item.location && (
                <p className="text-white/50 text-xs uppercase tracking-[0.15em] mt-1.5 flex items-center justify-center gap-2">
                  <MapPin size={12} className="text-accent" /> {lightbox.item.location}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
